import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../../i18n/getLocalized';
import MultiLangInput from '../../components/MultiLangInput/MultiLangInput';
import { invalidateCurrencyNames } from '../../hooks/useCurrencyNames';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiPlus, FiXCircle,
  FiDollarSign, FiLayers, FiFileText,
  FiX, FiCheck,
  FiExternalLink, FiZap, FiUsers, FiBriefcase,
  FiSearch, FiSettings, FiInbox, FiCopy, FiDatabase, FiGitBranch, FiPaperclip,
  FiBookOpen
} from 'react-icons/fi';
import { parametersAPI, workflowsAPI, companiesAPI } from '../../services/api';
import CompanyScopeSelect, { companyScopeLabel, isInScope, canScopeAllCompanies, defaultScopeForUser } from '../../components/CompanyScopeSelect/CompanyScopeSelect';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import { getErrorFields, getErrorMessage } from '../../utils/apiError';
import { hasPermission } from '../../utils/permissions';
import ContractTemplates from '../ContractTemplates/ContractTemplates';
import TemplateLibraryAdmin from '../TemplateLibraryAdmin/TemplateLibraryAdmin';
import { TEMPLATE_LIBRARY_ENABLED } from '../../config/features';
import SchemaList from '../SchemaList/SchemaList';
import PageHeader from '../../components/PageHeader/PageHeader';
import './AdminPanel.scss';

// Tabs that embed a full sub-page component instead of the generic CRUD table.
const EMBEDDED_TABS = ['templates', 'schemas', 'templateLibrary'];

// Valid ?tab= values, in the CLM-27 display order. Kept in sync with TABS
// below; used to validate the URL before trusting it.
const TAB_KEYS = [
  'mainGroups', 'contractTypes', 'schemas', 'templates', 'templateLibrary',
  'counterpartyTypes', 'counterparties', 'currencies', 'supportingDocTypes',
].filter((k) => TEMPLATE_LIBRARY_ENABLED || k !== 'templateLibrary');

// Parametre bazlı yetkilendirme: every Definitions tab carries its own manage
// permission; a user only reaches the tabs they hold. The embedded
// Configuration tabs (schemas/templates) keep their existing permissions.
const TAB_PERMISSIONS = {
  mainGroups: 'manage_contract_main_groups',
  contractTypes: 'manage_contract_types',
  schemas: 'manage_field_schemas',
  templates: 'manage_contract_templates',
  templateLibrary: 'manage_template_library',
  counterpartyTypes: 'manage_counterparty_types',
  counterparties: 'manage_counterparties',
  currencies: 'manage_currencies',
  supportingDocTypes: 'manage_supporting_doc_types',
};

// Fields the server rejects the record without, per tab. Checked before the
// request so the user sees which input is missing instead of a round-trip
// "Validation Error", and used to label the server's own field errors.
const REQUIRED_FIELDS = {
  currencies: ['code', 'name'],
  supportingDocTypes: ['name'],
  mainGroups: ['code', 'name'],
  contractTypes: ['name', 'mainGroupId', 'workflowId'],
  counterpartyTypes: ['code', 'name'],
  counterparties: ['counterpartyTypeId', 'code', 'name'],
  approvalRoles: ['roleId', 'roleCode', 'roleName'],
  flowControls: ['contractTypeId', 'roleId'],
};

// Schema path → the i18n key of the label shown above that input. Paths the
// user cannot see (createdBy, code on contract types — server-generated) are
// deliberately absent; getErrorMessage falls back to the server message then.
const FIELD_LABEL_KEYS = {
  currencies: { code: 'currencyCode', name: 'displayName' },
  supportingDocTypes: { code: 'typeCode', name: 'typeName' },
  mainGroups: { code: 'groupCode', name: 'groupName' },
  contractTypes: { name: 'contractTypeName', mainGroupId: 'mainGroup' },
  counterpartyTypes: { code: 'typeCode', name: 'typeName' },
  counterparties: {
    counterpartyTypeId: 'counterpartyType', code: 'code', name: 'name',
    taxOffice: 'taxOffice', taxNumber: 'taxNumber', country: 'country', city: 'city',
    district: 'district', postalCode: 'postalCode', address: 'address', phone: 'phone',
    mobilePhone: 'mobilePhone', fax: 'fax', email: 'email', website: 'website',
    contactPerson: 'contactPerson', bankName: 'bankName',
    bankAccountNumber: 'accountNumber', iban: 'iban',
  },
  approvalRoles: { roleId: 'roleId', roleCode: 'roleCode', roleName: 'roleName' },
  flowControls: {
    contractTypeId: 'contractType', roleId: 'baseApproverRole',
    firstManagerRoleId: 'firstManager', secondManagerRoleId: 'secondManager',
    thirdManagerRoleId: 'thirdManager',
  },
};

// A multilang value counts as filled when at least one language has text.
const isBlankValue = (v) => {
  if (v == null) return true;
  if (typeof v === 'string') return !v.trim();
  if (typeof v === 'object') return !Object.values(v).some(x => typeof x === 'string' && x.trim());
  return false;
};


// ─── Modal Wrapper ───
function Modal({ open, onClose, title, subtitle, icon: Icon, wide, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="admin-modal__backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className={`admin-modal ${wide ? 'admin-modal--wide' : ''}`} onClick={e => e.stopPropagation()}>
        <header className="admin-modal__header">
          <div className="admin-modal__title">
            {Icon && <span className="admin-modal__icon"><Icon /></span>}
            <div>
              <h3>{title}</h3>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
          <button className="admin-modal__close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </header>
        <div className="admin-modal__body">{children}</div>
        {footer && <div className="admin-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}


// ─── Field Schema Selector Panel (for Contract Types) ───
// scopeCompanyIds: the contract type's company scope — only schemas visible
// to that whole scope are offered. A pre-existing selection that a LATER
// narrowing of the schema's own scope left "out of scope" is preserved and
// shown disabled (parents may have been re-scoped after this record was
// created); the selection is only cleared when the USER changes the scope
// in this very modal session.
function FieldSchemaSelector({ fieldSchemaId, onChange, navigate, scopeCompanyIds }) {
  const { t, i18n } = useTranslation();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const scopeKey = JSON.stringify([...(scopeCompanyIds || [])].map(String).sort());
  const initialScopeKeyRef = useRef(scopeKey);

  useEffect(() => {
    setLoading(true);
    parametersAPI.fieldSchemas.list({ active: 'true' })
      .then(r => { setSchemas(r.data.items || []); setLoaded(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visibleSchemas = useMemo(
    () => schemas.filter(s => isInScope(s.companyIds, scopeCompanyIds)),
    [schemas, scopeCompanyIds]
  );

  // Clear a selection the current scope cannot see — but only after the list
  // has loaded, and only if the scope was changed IN THIS MODAL (pre-existing
  // drift must survive an untouched open-and-save).
  useEffect(() => {
    if (!loaded || !fieldSchemaId) return;
    if (scopeKey === initialScopeKeyRef.current) return;
    if (!visibleSchemas.some(s => s._id === fieldSchemaId)) onChange(null);
  }, [loaded, fieldSchemaId, scopeKey, visibleSchemas]); // onChange deliberately omitted — recreated every render

  const selectedSchema = schemas.find(s => s._id === fieldSchemaId);

  return (
    <div className="schema-panel">
      <div className="schema-panel__head">
        <span className="schema-panel__icon"><FiZap /></span>
        <div>
          <h5>{t('adminPanel.fieldsManagerSchema')}</h5>
          <p>{t('adminPanel.attachSchema')}</p>
        </div>
      </div>

      <div className="schema-panel__row">
        <select
          value={fieldSchemaId || ''}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">{t('adminPanel.noCustomSchema')}</option>
          {loading && <option disabled>{t('adminPanel.loadingSchemas')}</option>}
          {visibleSchemas.map(s => (
            <option key={s._id} value={s._id}>{getLocalized(s.name, i18n.language)}</option>
          ))}
        </select>
        {selectedSchema && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => navigate(`/fields-manager/${fieldSchemaId}`)}
          >
            <FiExternalLink /> {t('adminPanel.editSchema')}
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline"
          onClick={() => navigate('/fields-manager')}
        >
          <FiPlus /> {t('adminPanel.newSchema')}
        </button>
      </div>

      {selectedSchema && (
        <div className="schema-panel__selected">
          <FiCheck />
          <span><strong>{getLocalized(selectedSchema.name, i18n.language)}</strong>
            {selectedSchema.description && (
              <em> — {getLocalized(selectedSchema.description, i18n.language)}</em>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Workflow Template Selector Panel (for Contract Types) ───
// The workflow template is mandatory: a contract type cannot be saved
// without one (the standard workflow is always available in the list).
function WorkflowSelector({ workflowId, onChange, navigate, error, scopeCompanyIds }) {
  const { t, i18n } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const scopeKey = JSON.stringify([...(scopeCompanyIds || [])].map(String).sort());
  const initialScopeKeyRef = useRef(scopeKey);

  useEffect(() => {
    setLoading(true);
    // workflowsAPI.list() returns active workflows only.
    workflowsAPI.list()
      .then(r => { setWorkflows(r.data.workflows || []); setLoaded(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Same scope filtering / drift preservation as FieldSchemaSelector.
  const visibleWorkflows = useMemo(
    () => workflows.filter(w => isInScope(w.companyIds, scopeCompanyIds)),
    [workflows, scopeCompanyIds]
  );

  useEffect(() => {
    if (!loaded || !workflowId) return;
    if (scopeKey === initialScopeKeyRef.current) return;
    if (!visibleWorkflows.some(w => w._id === workflowId)) onChange(null);
  }, [loaded, workflowId, scopeKey, visibleWorkflows]); // onChange deliberately omitted — recreated every render

  const selectedWorkflow = workflows.find(w => w._id === workflowId);

  return (
    <div className="schema-panel">
      <div className="schema-panel__head">
        <span className="schema-panel__icon"><FiGitBranch /></span>
        <div>
          <h5>{t('adminPanel.workflowTemplate')} <span className="req">*</span></h5>
          <p>{t('adminPanel.attachWorkflow')}</p>
        </div>
      </div>

      <div className="schema-panel__row">
        <select
          value={workflowId || ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={error ? 'has-error' : ''}
        >
          <option value="">{t('adminPanel.selectWorkflowTemplate', '-- İş akışı şablonu seçin --')}</option>
          {loading && <option disabled>{t('adminPanel.loadingWorkflows')}</option>}
          {visibleWorkflows.map(w => (
            <option key={w._id} value={w._id}>{localizeSeeded(w.name, i18n.language)}</option>
          ))}
        </select>
        {selectedWorkflow && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => navigate(`/flow-management/${workflowId}/edit`)}
          >
            <FiExternalLink /> {t('adminPanel.editWorkflow')}
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline"
          onClick={() => navigate('/flow-management/new')}
        >
          <FiPlus /> {t('adminPanel.newWorkflow')}
        </button>
      </div>

      {error && (
        <p className="schema-panel__error">{t('contractForm.requiredField', 'Bu alan zorunludur')}</p>
      )}

      {selectedWorkflow && (
        <div className="schema-panel__selected">
          <FiCheck />
          <span><strong>{localizeSeeded(selectedWorkflow.name, i18n.language)}</strong>
            {selectedWorkflow.description && <em> — {localizeSeeded(selectedWorkflow.description, i18n.language)}</em>}
          </span>
        </div>
      )}
    </div>
  );
}

function AdminPanel() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Company-scope authority: owners/admins may define for "All Companies";
  // everyone else creates records pinned to their own company.
  const authUser = useSelector((s) => s.auth?.user || null);
  const scopePrivileged = canScopeAllCompanies(authUser);
  const defaultScopeIds = defaultScopeForUser(authUser);
  // The app sidebar deep-links into a tab via ?tab=; the URL is the source of
  // truth so those links highlight and back/forward move between tabs.
  // Only tabs whose manage permission the user holds are reachable — a
  // deep-link to a forbidden tab falls back to the first permitted one.
  const [searchParams] = useSearchParams();
  const allowedTabKeys = TAB_KEYS.filter((key) =>
    hasPermission(authUser?.role, TAB_PERMISSIONS[key], authUser?.effectivePermissions));
  const requestedTab = searchParams.get('tab');
  const activeTab = allowedTabKeys.includes(requestedTab)
    ? requestedTab
    : (allowedTabKeys[0] || 'mainGroups');
  const [items, setItems] = useState([]);
  // Which tab `items` was fetched for. A tab switch re-renders before the
  // load effect runs, so this is what keeps one tab's rows out of another
  // tab's table — clearing state in the effect would be one render too late.
  const [itemsTab, setItemsTab] = useState(activeTab);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // Per-field save errors, keyed by form field name → kind ('required',
  // 'duplicate', 'maxlength', …). Filled by the pre-save check and by the
  // `fields` list the API returns with a validation error.
  const [fieldErrors, setFieldErrors] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  // Supporting data
  const [mainGroups, setMainGroups] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [approvalRoles, setApprovalRoles] = useState([]);
  const [counterpartyTypes, setCounterpartyTypes] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Order and labels are fixed by CLM-27 — Contract Categories → Types →
  // Fields → Templates → Counterparty Types → Counterparties → Currencies.
  const TABS = useMemo(() => [
    { key: 'mainGroups',        icon: FiLayers,     label: t('adminPanel.tabs.mainGroups'),        singular: t('adminPanel.tabsSingular.mainGroups') },
    { key: 'contractTypes',     icon: FiFileText,   label: t('adminPanel.tabs.contractTypes'),     singular: t('adminPanel.tabsSingular.contractTypes') },
    { key: 'schemas',           icon: FiDatabase,   label: t('adminPanel.tabs.schemas'),           singular: t('adminPanel.tabsSingular.schemas') },
    { key: 'templates',         icon: FiCopy,       label: t('adminPanel.tabs.templates'),         singular: t('adminPanel.tabsSingular.templates') },
    ...(TEMPLATE_LIBRARY_ENABLED
      ? [{ key: 'templateLibrary', icon: FiBookOpen, label: t('adminPanel.tabs.templateLibrary'), singular: t('adminPanel.tabsSingular.templateLibrary') }]
      : []),
    { key: 'counterpartyTypes', icon: FiBriefcase,  label: t('adminPanel.tabs.counterpartyTypes'), singular: t('adminPanel.tabsSingular.counterpartyTypes') },
    { key: 'counterparties',    icon: FiUsers,      label: t('adminPanel.tabs.counterparties'),    singular: t('adminPanel.tabsSingular.counterparties') },
    { key: 'currencies',        icon: FiDollarSign, label: t('adminPanel.tabs.currencies'),        singular: t('adminPanel.tabsSingular.currencies') },
    { key: 'supportingDocTypes', icon: FiPaperclip,  label: t('adminPanel.tabs.supportingDocTypes'), singular: t('adminPanel.tabsSingular.supportingDocTypes') },
  ], [t]);

  // Guards against a slower earlier request overwriting a newer tab's rows.
  const loadTokenRef = useRef(0);

  const loadItems = useCallback(async () => {
    const token = ++loadTokenRef.current;

    // Drop the previous tab's rows immediately. Each tab renders its own row
    // markup against a different entity shape, so showing one tab's items in
    // another tab's table is never merely cosmetic — it reads fields that do
    // not exist there and can crash the render.
    setItems([]);
    setItemsTab(activeTab);

    // Embedded tabs (templates / schemas) manage their own data.
    if (EMBEDDED_TABS.includes(activeTab)) { setLoading(false); return; }
    setLoading(true);
    try {
      let res;
      switch (activeTab) {
        case 'currencies': res = await parametersAPI.currencies.list(); break;
        case 'supportingDocTypes': res = await parametersAPI.supportingDocTypes.list(); break;
        case 'mainGroups': res = await parametersAPI.contractMainGroups.list(); break;
        case 'contractTypes': res = await parametersAPI.contractTypes.list(); break;
        case 'counterpartyTypes': res = await parametersAPI.counterpartyTypes.list(); break;
        case 'counterparties': res = await parametersAPI.counterparties.list(); break;
        case 'approvalRoles': res = await parametersAPI.approvalRoles.list(); break;
        case 'flowControls': res = await parametersAPI.flowControls.list(); break;
        default: res = { data: { items: [] } };
      }
      if (token !== loadTokenRef.current) return;   // superseded by a newer tab
      setItems(res.data.items || []);
      setItemsTab(activeTab);
    } catch {
      if (token === loadTokenRef.current) toast.error(t('adminPanel.failedToLoad'));
    }
    if (token === loadTokenRef.current) setLoading(false);
  }, [activeTab, t]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { setQuery(''); }, [activeTab]);

  const loadSupportingData = useCallback(() => {
    parametersAPI.contractMainGroups.list({ active: 'true' }).then(r => setMainGroups(r.data.items || [])).catch(() => {});
    parametersAPI.contractTypes.list({ active: 'true' }).then(r => setContractTypes(r.data.items || [])).catch(() => {});
    parametersAPI.approvalRoles.list({ active: 'true' }).then(r => setApprovalRoles(r.data.items || [])).catch(() => {});
    parametersAPI.counterpartyTypes.list({ active: 'true' }).then(r => setCounterpartyTypes(r.data.items || [])).catch(() => {});
    companiesAPI.list().then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, []);

  useEffect(() => { loadSupportingData(); }, [loadSupportingData]);

  // Editing a field clears its error, so the message disappears as soon as the
  // user addresses it rather than lingering until the next save.
  const clearFieldError = useCallback((name) => {
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    clearFieldError(name);
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCreate = () => {
    setEditItem(null);
    setFieldErrors({});
    setForm(getDefaultForm());
    setModalOpen(true);
  };

  const openEdit = async (item) => {
    setEditItem(item);
    setFieldErrors({});
    if (activeTab === 'contractTypes' && item._id) {
      try {
        const res = await parametersAPI.contractTypes.get(item._id);
        setForm(itemToForm(res.data.item));
      } catch {
        setForm(itemToForm(item));
      }
    } else {
      setForm(itemToForm(item));
    }
    setModalOpen(true);
  };

  const getDefaultForm = () => {
    // New scoped records default to the creator's authority: [] (All
    // Companies) for owners/admins, the user's own company for everyone else.
    const companyIds = [...defaultScopeIds];
    switch (activeTab) {
      case 'currencies': return { code: '', name: '', companyIds, isActive: true };
      case 'supportingDocTypes': return { code: '', name: '', description: '', companyIds, isActive: true };
      case 'mainGroups': return { code: '', name: '', companyIds, isActive: true };
      case 'contractTypes': return { name: '', mainGroupId: '', companyIds, isActive: true };
      case 'counterpartyTypes': return { code: '', name: '', companyIds, isActive: true };
      case 'counterparties': return { counterpartyTypeId: '', code: '', name: '', companyIds, taxOffice: '', taxNumber: '', country: '', city: '', district: '', postalCode: '', address: '', phone: '', mobilePhone: '', fax: '', email: '', website: '', contactPerson: '', bankName: '', bankAccountNumber: '', iban: '', isActive: true };
      case 'approvalRoles': return { roleId: '', roleCode: '', roleName: '', isActive: true };
      case 'flowControls': return { contractTypeId: '', roleId: '', firstManagerRoleId: '', secondManagerRoleId: '', thirdManagerRoleId: '', requireLegalApproval: false, isActive: true };
      default: return {};
    }
  };

  const itemToForm = (item) => {
    const base = { ...item };
    // Normalize company scope refs (may arrive populated) to plain id strings.
    base.companyIds = (item.companyIds || []).map(v => String(v?._id || v));
    if (activeTab === 'contractTypes') {
      base.mainGroupId = item.mainGroupId?._id || item.mainGroupId || '';
      base.fieldSchemaId = item.fieldSchemaId?._id || item.fieldSchemaId || null;
      base.workflowId = item.workflowId?._id || item.workflowId || null;
    }
    if (activeTab === 'counterparties') {
      base.counterpartyTypeId = item.counterpartyTypeId?._id || item.counterpartyTypeId || '';
    }
    if (activeTab === 'flowControls') {
      base.contractTypeId = item.contractTypeId?._id || item.contractTypeId || '';
      base.roleId = item.roleId?._id || item.roleId || '';
      base.firstManagerRoleId = item.firstManagerRoleId?._id || item.firstManagerRoleId || '';
      base.secondManagerRoleId = item.secondManagerRoleId?._id || item.secondManagerRoleId || '';
      base.thirdManagerRoleId = item.thirdManagerRoleId?._id || item.thirdManagerRoleId || '';
    }
    return base;
  };

  // Label shown to the user for a schema path, used to name the field a save
  // error belongs to. Returns '' for paths with no visible input.
  const labelFor = useCallback((path) => {
    if (path === 'workflowId') return t('adminPanel.workflowTemplate');
    if (path === 'fieldSchemaId') return t('adminPanel.fieldsManagerSchema');
    const key = FIELD_LABEL_KEYS[activeTab]?.[path];
    return key ? t(`adminPanel.fields.${key}`) : '';
  }, [activeTab, t]);

  const handleSave = async () => {
    // Catch the missing values here so the user is told which input is empty.
    // The server enforces the same rules — this only saves a round trip.
    const missing = (REQUIRED_FIELDS[activeTab] || [])
      .filter(f => isBlankValue(form[f]));
    if (missing.length) {
      setFieldErrors(Object.fromEntries(missing.map(f => [f, 'required'])));
      const labels = missing.map(f => labelFor(f)).filter(Boolean);
      toast.error(labels.length
        ? t('adminPanel.validation.requiredFields', { fields: labels.join(', ') })
        : t('adminPanel.validation.required'));
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const data = { ...form };
      if (activeTab === 'flowControls') {
        ['firstManagerRoleId', 'secondManagerRoleId', 'thirdManagerRoleId'].forEach(k => { if (!data[k]) data[k] = null; });
      }
      delete data._id; delete data.__v; delete data.createdAt; delete data.updatedAt; delete data.createdBy; delete data.updatedBy;

      if (editItem) {
        switch (activeTab) {
          case 'currencies': await parametersAPI.currencies.update(editItem._id, data); break;
          case 'supportingDocTypes': await parametersAPI.supportingDocTypes.update(editItem._id, data); break;
          case 'mainGroups': await parametersAPI.contractMainGroups.update(editItem._id, data); break;
          case 'contractTypes': await parametersAPI.contractTypes.update(editItem._id, data); break;
          case 'counterpartyTypes': await parametersAPI.counterpartyTypes.update(editItem._id, data); break;
          case 'counterparties': await parametersAPI.counterparties.update(editItem._id, data); break;
          case 'approvalRoles': await parametersAPI.approvalRoles.update(editItem._id, data); break;
          case 'flowControls': await parametersAPI.flowControls.update(editItem._id, data); break;
          default: break;
        }
        toast.success(t('adminPanel.updatedSuccessfully'));
      } else {
        switch (activeTab) {
          case 'currencies': await parametersAPI.currencies.create(data); break;
          case 'supportingDocTypes': await parametersAPI.supportingDocTypes.create(data); break;
          case 'mainGroups': await parametersAPI.contractMainGroups.create(data); break;
          case 'contractTypes': await parametersAPI.contractTypes.create(data); break;
          case 'counterpartyTypes': await parametersAPI.counterpartyTypes.create(data); break;
          case 'counterparties': await parametersAPI.counterparties.create(data); break;
          case 'approvalRoles': await parametersAPI.approvalRoles.create(data); break;
          case 'flowControls': await parametersAPI.flowControls.create(data); break;
          default: break;
        }
        toast.success(t('adminPanel.createdSuccessfully'));
      }
      // Currency display names are cached process-wide for the screens that
      // label an amount's currency — drop it so an edit shows up there too.
      if (activeTab === 'currencies') invalidateCurrencyNames();
      setModalOpen(false);
      loadItems();
      loadSupportingData();
    } catch (err) {
      // The API names the offending fields; mark them inline and say which
      // ones they are instead of echoing a bare "Validation Error".
      setFieldErrors(getErrorFields(err));
      toast.error(getErrorMessage(err, {
        t,
        labelFor,
        fallback: t('adminPanel.failedToSave'),
      }));
    }
    setSaving(false);
  };

  const handleDeactivate = async (item) => {
    if (!window.confirm(t('adminPanel.deactivateConfirm'))) return;
    try {
      switch (activeTab) {
        case 'currencies': await parametersAPI.currencies.deactivate(item._id); break;
        case 'supportingDocTypes': await parametersAPI.supportingDocTypes.deactivate(item._id); break;
        case 'mainGroups': await parametersAPI.contractMainGroups.deactivate(item._id); break;
        case 'contractTypes': await parametersAPI.contractTypes.deactivate(item._id); break;
        case 'counterpartyTypes': await parametersAPI.counterpartyTypes.deactivate(item._id); break;
        case 'counterparties': await parametersAPI.counterparties.deactivate(item._id); break;
        case 'approvalRoles': await parametersAPI.approvalRoles.deactivate(item._id); break;
        case 'flowControls': await parametersAPI.flowControls.deactivate(item._id); break;
        default: break;
      }
      toast.success(t('adminPanel.deactivated'));
      loadItems();
    } catch { toast.error(t('adminPanel.failedToDeactivate')); }
  };

  // ─── Counterparty type options ───
  // Only Active types may be *chosen*. But if the record being edited already
  // points at a type that has since been deactivated, that type is appended
  // (disabled) so the existing relationship stays visible and is preserved on
  // save — deactivating a type must not silently rewrite historical records.
  const counterpartyTypeOptions = useMemo(() => {
    // Only types visible to the counterparty's whole company scope are
    // offered — the scope is chosen first, dependent lists follow it.
    const active = counterpartyTypes
      .filter(ct => isInScope(ct.companyIds, form.companyIds))
      .map(ct => ({ ...ct, inactive: false }));
    const currentId = form.counterpartyTypeId;
    if (!currentId || active.some(ct => String(ct._id) === String(currentId))) return active;

    const current = editItem?.counterpartyTypeId;
    return [
      ...active,
      typeof current === 'object' && current
        ? { ...current, inactive: true }
        : { _id: currentId, name: '', code: '', inactive: true },
    ];
  }, [counterpartyTypes, form.counterpartyTypeId, form.companyIds, editItem]);

  // ─── Filter items ───
  // Each tab renders its own row markup against a different entity shape, so
  // rows from the previous tab must never reach the table — not even for the
  // single render between the tab switch and the fetch.
  const safeItems = itemsTab === activeTab ? items : [];

  const filteredItems = useMemo(() => {
    if (!query.trim()) return safeItems;
    const q = query.toLowerCase();
    return safeItems.filter(i => JSON.stringify(i).toLowerCase().includes(q));
  }, [safeItems, query]);

  const activeCount = useMemo(() => safeItems.filter(i => i.isActive).length, [safeItems]);

  // ─── Field-level error rendering ───
  const errClass = (k) => (fieldErrors[k] ? 'has-error' : '');

  const FieldError = ({ k }) => {
    const kind = fieldErrors[k];
    if (!kind) return null;
    const msg = kind === 'required' ? t('adminPanel.validation.required')
      : kind === 'duplicate' ? t('adminPanel.validation.duplicate')
        : kind === 'maxlength' ? t('adminPanel.validation.tooLong')
          : t('adminPanel.validation.invalid');
    return <p className="field-error">{msg}</p>;
  };

  // ─── Company scope (Tüm Şirketler / belirli şirketler) ───
  // Shared by every scoped definition tab. Empty selection = All Companies.
  // Dependent pickers (contract type → main group / schema / workflow) are
  // filtered by this scope, so a scope change drops any selection the new
  // scope can no longer see. Schema/workflow selections are cleared by their
  // selector panels; the main-group select is cleared here.
  const handleScopeChange = (ids) => {
    setForm(prev => {
      const next = { ...prev, companyIds: ids };
      if (activeTab === 'contractTypes' && next.mainGroupId) {
        const g = mainGroups.find(x => x._id === next.mainGroupId);
        if (!g || !isInScope(g.companyIds, ids)) next.mainGroupId = '';
      }
      if (activeTab === 'counterparties' && next.counterpartyTypeId) {
        const ct = counterpartyTypes.find(x => String(x._id) === String(next.counterpartyTypeId));
        if (!ct || !isInScope(ct.companyIds, ids)) next.counterpartyTypeId = '';
      }
      return next;
    });
  };
  // The compact bar carries its own inline label — no FormSection wrapper.
  // Rendered as the FIRST control of every scoped form: pick the scope, then
  // everything below is offered/filtered accordingly.
  const scopeSection = (
    <div className="scope-bar">
      <CompanyScopeSelect
        value={form.companyIds || []}
        onChange={handleScopeChange}
        companies={companies}
        restrictToCompanyIds={scopePrivileged ? null : defaultScopeIds}
      />
    </div>
  );

  // ─── Modal Form Content ───
  const renderModalForm = () => {
    switch (activeTab) {
      case 'currencies':
        return (
          <div className="form-section">
            {scopeSection}
            <div className="form-grid">
              {/* The ISO code is technical and identical in every language, so
                  it stays a plain input; only the display name is multilang. */}
              <div className="form-group"><label>{t('adminPanel.fields.currencyCode')} <span className="req">*</span></label><input name="code" className={errClass('code')} value={form.code || ''} onChange={handleChange} maxLength={10} placeholder="TRY, USD, EUR" autoFocus /><FieldError k="code" /></div>
              <div className="form-group">
                <label>{t('adminPanel.fields.displayName')} <span className="req">*</span></label>
                <MultiLangInput
                  value={form.name || ''}
                  onChange={(v) => { clearFieldError('name'); setForm((prev) => ({ ...prev, name: v })); }}
                  primaryLang={i18n.language}
                  placeholder={t('adminPanel.fields.currencyNamePlaceholder')}
                  inputClassName={errClass('name')}
                />
                <FieldError k="name" />
              </div>
            </div>
            <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
          </div>
        );
      case 'mainGroups':
        return (
          <div className="form-section">
            {scopeSection}
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.groupCode')} <span className="req">*</span></label><input name="code" className={errClass('code')} value={form.code || ''} onChange={handleChange} maxLength={20} placeholder="COM, HR, IT" autoFocus /><FieldError k="code" /></div>
              <div className="form-group"><label>{t('adminPanel.fields.groupName')} <span className="req">*</span></label><input name="name" className={errClass('name')} value={form.name || ''} onChange={handleChange} maxLength={200} placeholder="Commercial Agreements" /><FieldError k="name" /></div>
            </div>
            <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
          </div>
        );
      case 'supportingDocTypes':
        return (
          <div className="form-section">
            {scopeSection}
            <div className="form-grid">
              <div className="form-group">
                <label>{t('adminPanel.fields.typeName')} <span className="req">*</span></label>
                <MultiLangInput
                  value={form.name || ''}
                  onChange={(v) => { clearFieldError('name'); setForm((prev) => ({ ...prev, name: v })); }}
                  primaryLang={i18n.language}
                  placeholder={t('adminPanel.fields.supportingDocTypePlaceholder', { defaultValue: 'Teminat Mektubu, Sigorta Poliçesi…' })}
                  inputClassName={errClass('name')}
                />
                <FieldError k="name" />
              </div>
              <div className="form-group"><label>{t('adminPanel.fields.typeCode')}</label><input name="code" className={errClass('code')} value={form.code || ''} onChange={handleChange} maxLength={20} placeholder="TMN, SGR" /><FieldError k="code" /></div>
              <div className="form-group form-group--full"><label>{t('adminPanel.fields.description')}</label><input name="description" value={form.description || ''} onChange={handleChange} maxLength={500} /></div>
            </div>
            <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
          </div>
        );
      case 'counterpartyTypes':
        return (
          <div className="form-section">
            {scopeSection}
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.typeCode')} <span className="req">*</span></label><input name="code" className={errClass('code')} value={form.code || ''} onChange={handleChange} maxLength={20} placeholder="VND, CUS, RSL" autoFocus /><FieldError k="code" /></div>
              <div className="form-group">
                <label>{t('adminPanel.fields.typeName')} <span className="req">*</span></label>
                <MultiLangInput
                  value={form.name || ''}
                  onChange={(v) => { clearFieldError('name'); setForm((prev) => ({ ...prev, name: v })); }}
                  primaryLang={i18n.language}
                  placeholder="Vendor, Customer"
                  inputClassName={errClass('name')}
                />
                <FieldError k="name" />
              </div>
            </div>
            <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
          </div>
        );
      case 'counterparties':
        return (<>
          {scopeSection}
          <FormSection title={t('adminPanel.fields.name')}>
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.counterpartyType')} <span className="req">*</span></label>
                <select name="counterpartyTypeId" className={errClass('counterpartyTypeId')} value={form.counterpartyTypeId || ''} onChange={handleChange} required>
                  <option value="">{t('adminPanel.fields.selectType')}</option>
                  {counterpartyTypeOptions.map(ct => (
                    <option key={ct._id} value={ct._id} disabled={ct.inactive}>
                      {getLocalized(ct.name, i18n.language)} ({ct.code})
                      {ct.inactive ? ` — ${t('common.inactive')}` : ''}
                    </option>
                  ))}
                </select>
                <FieldError k="counterpartyTypeId" />
              </div>
              <div className="form-group"><label>{t('adminPanel.fields.code')} <span className="req">*</span></label><input name="code" className={errClass('code')} value={form.code || ''} onChange={handleChange} maxLength={50} placeholder="CP001" autoFocus /><FieldError k="code" /></div>
              <div className="form-group form-group--full"><label>{t('adminPanel.fields.name')} <span className="req">*</span></label><input name="name" className={errClass('name')} value={form.name || ''} onChange={handleChange} maxLength={200} placeholder="Company or individual name" /><FieldError k="name" /></div>
            </div>
          </FormSection>

          <FormSection title={t('adminPanel.fields.taxInformation')}>
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.taxOffice')}</label><input name="taxOffice" value={form.taxOffice || ''} onChange={handleChange} maxLength={100} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.taxNumber')}</label><input name="taxNumber" value={form.taxNumber || ''} onChange={handleChange} maxLength={20} /></div>
            </div>
          </FormSection>

          <FormSection title={t('adminPanel.fields.address')}>
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.country')}</label><input name="country" value={form.country || ''} onChange={handleChange} maxLength={100} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.city')}</label><input name="city" value={form.city || ''} onChange={handleChange} maxLength={100} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.district')}</label><input name="district" value={form.district || ''} onChange={handleChange} maxLength={100} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.postalCode')}</label><input name="postalCode" value={form.postalCode || ''} onChange={handleChange} maxLength={20} /></div>
              <div className="form-group form-group--full"><label>{t('adminPanel.fields.address')}</label><input name="address" value={form.address || ''} onChange={handleChange} maxLength={500} placeholder="Street address" /></div>
            </div>
          </FormSection>

          <FormSection title={t('adminPanel.fields.contact')}>
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.phone')}</label><input name="phone" value={form.phone || ''} onChange={handleChange} maxLength={30} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.mobilePhone')}</label><input name="mobilePhone" value={form.mobilePhone || ''} onChange={handleChange} maxLength={30} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.fax')}</label><input name="fax" value={form.fax || ''} onChange={handleChange} maxLength={30} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.email')}</label><input name="email" value={form.email || ''} onChange={handleChange} maxLength={150} type="email" /></div>
              <div className="form-group"><label>{t('adminPanel.fields.website')}</label><input name="website" value={form.website || ''} onChange={handleChange} maxLength={200} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.contactPerson')}</label><input name="contactPerson" value={form.contactPerson || ''} onChange={handleChange} maxLength={200} /></div>
            </div>
          </FormSection>

          <FormSection title={t('adminPanel.fields.banking')}>
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.bankName')}</label><input name="bankName" value={form.bankName || ''} onChange={handleChange} maxLength={100} /></div>
              <div className="form-group"><label>{t('adminPanel.fields.accountNumber')}</label><input name="bankAccountNumber" value={form.bankAccountNumber || ''} onChange={handleChange} maxLength={50} /></div>
              <div className="form-group form-group--full"><label>{t('adminPanel.fields.iban')}</label><input name="iban" value={form.iban || ''} onChange={handleChange} maxLength={34} placeholder="TR00 0000 0000 0000 0000 0000 00" /></div>
            </div>
          </FormSection>

          <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
        </>);
      case 'contractTypes': {
        // Field order is deliberate: the company scope comes first because the
        // main-group list (and the schema/workflow panels below) only offer
        // definitions visible to the whole selected scope.
        const scopedMainGroups = mainGroups.filter(g => isInScope(g.companyIds, form.companyIds));
        return (<>
          <div className="form-section">
            {scopeSection}
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.mainGroup')} <span className="req">*</span></label>
                <select name="mainGroupId" className={errClass('mainGroupId')} value={form.mainGroupId || ''} onChange={handleChange}>
                  <option value="">{t('adminPanel.fields.selectMainGroup')}</option>
                  {scopedMainGroups.map(g => <option key={g._id} value={g._id}>{g.name} ({g.code})</option>)}
                </select>
                <FieldError k="mainGroupId" />
              </div>
              <div className="form-group">
                <label>{t('adminPanel.fields.contractTypeName')} <span className="req">*</span></label>
                <MultiLangInput
                  value={form.name || ''}
                  onChange={(v) => { clearFieldError('name'); setForm((prev) => ({ ...prev, name: v })); }}
                  primaryLang={i18n.language}
                  placeholder="Service Agreement, NDA"
                  inputClassName={errClass('name')}
                />
                <FieldError k="name" />
              </div>
            </div>
            {editItem && (
              <div className="form-group">
                <label>{t('adminPanel.fields.autoGeneratedCode')}</label>
                <input value={form.code || ''} disabled className="input-readonly" />
              </div>
            )}
            <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
          </div>
          <FieldSchemaSelector
            fieldSchemaId={form.fieldSchemaId || null}
            onChange={(id) => setForm(prev => ({ ...prev, fieldSchemaId: id }))}
            navigate={navigate}
            scopeCompanyIds={form.companyIds || []}
          />
          <WorkflowSelector
            workflowId={form.workflowId || null}
            onChange={(id) => { clearFieldError('workflowId'); setForm(prev => ({ ...prev, workflowId: id })); }}
            navigate={navigate}
            error={!!fieldErrors.workflowId}
            scopeCompanyIds={form.companyIds || []}
          />
        </>);
      }
      case 'approvalRoles':
        return (
          <div className="form-section">
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.roleId')} <span className="req">*</span></label><input name="roleId" className={errClass('roleId')} value={form.roleId || ''} onChange={handleChange} maxLength={20} autoFocus placeholder="R01" /><FieldError k="roleId" /></div>
              <div className="form-group"><label>{t('adminPanel.fields.roleCode')} <span className="req">*</span></label><input name="roleCode" className={errClass('roleCode')} value={form.roleCode || ''} onChange={handleChange} maxLength={50} placeholder="LEGAL_APPROVER" /><FieldError k="roleCode" /></div>
              <div className="form-group form-group--full"><label>{t('adminPanel.fields.roleName')} <span className="req">*</span></label><input name="roleName" className={errClass('roleName')} value={form.roleName || ''} onChange={handleChange} maxLength={200} placeholder="Legal Approver" /><FieldError k="roleName" /></div>
            </div>
            <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
          </div>
        );
      case 'flowControls':
        return (
          <div className="form-section">
            <div className="form-grid">
              <div className="form-group"><label>{t('adminPanel.fields.contractType')} <span className="req">*</span></label>
                <select name="contractTypeId" className={errClass('contractTypeId')} value={form.contractTypeId || ''} onChange={handleChange}>
                  <option value="">{t('adminPanel.fields.selectContractType')}</option>
                  {contractTypes.map(ct => <option key={ct._id} value={ct._id}>{getLocalized(ct.name, i18n.language)}</option>)}
                </select>
                <FieldError k="contractTypeId" />
              </div>
              <div className="form-group"><label>{t('adminPanel.fields.baseApproverRole')} <span className="req">*</span></label>
                <select name="roleId" className={errClass('roleId')} value={form.roleId || ''} onChange={handleChange}>
                  <option value="">{t('adminPanel.fields.selectRole')}</option>
                  {approvalRoles.map(r => <option key={r._id} value={r._id}>{r.roleName} ({r.roleCode})</option>)}
                </select>
                <FieldError k="roleId" />
              </div>
              <div className="form-group"><label>{t('adminPanel.fields.firstManager')}</label>
                <select name="firstManagerRoleId" value={form.firstManagerRoleId || ''} onChange={handleChange}>
                  <option value="">{t('adminPanel.fields.none')}</option>
                  {approvalRoles.map(r => <option key={r._id} value={r._id}>{r.roleName}</option>)}
                </select>
              </div>
              <div className="form-group"><label>{t('adminPanel.fields.secondManager')}</label>
                <select name="secondManagerRoleId" value={form.secondManagerRoleId || ''} onChange={handleChange}>
                  <option value="">{t('adminPanel.fields.none')}</option>
                  {approvalRoles.map(r => <option key={r._id} value={r._id}>{r.roleName}</option>)}
                </select>
              </div>
              <div className="form-group"><label>{t('adminPanel.fields.thirdManager')}</label>
                <select name="thirdManagerRoleId" value={form.thirdManagerRoleId || ''} onChange={handleChange}>
                  <option value="">{t('adminPanel.fields.none')}</option>
                  {approvalRoles.map(r => <option key={r._id} value={r._id}>{r.roleName}</option>)}
                </select>
              </div>
            </div>
            <div className="toggle-row">
              <ActiveToggle value={form.requireLegalApproval || false} onChange={handleChange} name="requireLegalApproval" label={t('adminPanel.fields.requireLegalApproval')} />
              <ActiveToggle value={form.isActive ?? true} onChange={handleChange} label={t('adminPanel.fields.active')} />
            </div>
          </div>
        );
      default: return null;
    }
  };

  // ─── Table Rendering ───
  const renderTable = () => {
    if (loading) return (
      <div className="state-box">
        <div className="state-box__spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
    if (!filteredItems.length) return (
      <div className="state-box">
        <div className="state-box__icon">
          <FiInbox />
        </div>
        <h3>
          {query
            ? t('adminPanel.empty.noMatches')
            : t('adminPanel.empty.noItemsYet', { tab: currentTab?.label?.toLowerCase() || t('adminPanel.empty.items') })}
        </h3>
        <p>
          {query
            ? t('adminPanel.empty.tryDifferent')
            : t('adminPanel.empty.createFirst')}
        </p>
        {!query && (
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <FiPlus /> {t('adminPanel.newItem', { tab: currentTab?.singular })}
          </button>
        )}
      </div>
    );

    const StatusBadge = ({ active }) => (
      <span className={`badge badge-${active ? 'active' : 'archived'}`}>
        <span className={`badge__dot badge__dot--${active ? 'active' : 'muted'}`} />
        {active ? t('adminPanel.columns.active') : t('adminPanel.columns.inactive')}
      </span>
    );
    const Actions = ({ item }) => (
      <div className="row-actions">
        {item.isActive && (
          <button className="icon-btn icon-btn--danger" onClick={(e) => { e.stopPropagation(); handleDeactivate(item); }} title={t('adminPanel.deactivated')} aria-label="Deactivate">
            <FiXCircle />
          </button>
        )}
      </div>
    );

    // Every Definitions tab renders its own columns but shares this behaviour:
    // the row itself opens the record.
    const rowProps = (item) => ({
      className: 'clickable-row',
      tabIndex: 0,
      onClick: () => openEdit(item),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(item); }
      },
    });

    // Company scope badge — "Tüm Şirketler" or the listed companies.
    const ScopeBadge = ({ item }) => (
      <span className={`chip ${((item.companyIds || []).length === 0) ? 'chip--teal' : 'chip--indigo'}`}>
        {companyScopeLabel(item.companyIds, companies, t)}
      </span>
    );

    switch (activeTab) {
      case 'currencies':
        return (<table><thead><tr><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.name')}</th><th>{t('companyScope.column')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><code>{i.code}</code></td><td><strong>{getLocalized(i.name, i18n.language)}</strong></td><td><ScopeBadge item={i} /></td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'mainGroups':
        return (<table><thead><tr><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.name')}</th><th>{t('companyScope.column')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><code>{i.code}</code></td><td><strong>{i.name}</strong></td><td><ScopeBadge item={i} /></td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'supportingDocTypes':
        return (<table><thead><tr><th>{t('adminPanel.columns.name')}</th><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.description')}</th><th>{t('companyScope.column')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><strong>{getLocalized(i.name, i18n.language)}</strong></td><td>{i.code ? <code>{i.code}</code> : <span className="muted">—</span>}</td><td>{i.description || <span className="muted">—</span>}</td><td><ScopeBadge item={i} /></td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'counterpartyTypes':
        return (<table><thead><tr><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.name')}</th><th>{t('companyScope.column')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><code>{i.code}</code></td><td><strong>{getLocalized(i.name, i18n.language)}</strong></td><td><ScopeBadge item={i} /></td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'counterparties':
        return (<table><thead><tr><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.name')}</th><th>{t('adminPanel.columns.type')}</th><th>{t('companyScope.column')}</th><th>{t('adminPanel.columns.city')}</th><th>{t('adminPanel.columns.phone')}</th><th>{t('adminPanel.columns.email')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><code>{i.code}</code></td><td><strong>{i.name}</strong></td><td>{getLocalized(i.counterpartyTypeId?.name, i18n.language) || '—'}</td><td><ScopeBadge item={i} /></td><td>{i.city || '—'}</td><td>{i.phone || '—'}</td><td>{i.email || '—'}</td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'contractTypes':
        return (<table><thead><tr><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.name')}</th><th>{t('adminPanel.columns.mainGroup')}</th><th>{t('companyScope.column')}</th><th>{t('adminPanel.columns.schema')}</th><th>{t('adminPanel.columns.workflow')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><code>{i.code}</code></td><td><strong>{getLocalized(i.name, i18n.language)}</strong></td><td>{i.mainGroupId?.name || '—'}</td><td><ScopeBadge item={i} /></td><td>{i.fieldSchemaId ? <span className="chip chip--indigo"><FiZap size={10} /> {t('adminPanel.chips.schema')}</span> : <span className="muted">—</span>}</td><td>{i.workflowId ? <span className="chip chip--teal"><FiGitBranch size={10} /> {localizeSeeded(i.workflowId?.name, i18n.language) || t('adminPanel.chips.workflow')}</span> : <span className="muted">—</span>}</td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'approvalRoles':
        return (<table><thead><tr><th>{t('adminPanel.columns.id')}</th><th>{t('adminPanel.columns.code')}</th><th>{t('adminPanel.columns.name')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td>{i.roleId}</td><td><code>{i.roleCode}</code></td><td><strong>{i.roleName}</strong></td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      case 'flowControls':
        return (<table><thead><tr><th>{t('adminPanel.columns.contractType')}</th><th>{t('adminPanel.columns.role')}</th><th>{t('adminPanel.columns.firstMgr')}</th><th>{t('adminPanel.columns.secondMgr')}</th><th>{t('adminPanel.columns.thirdMgr')}</th><th>{t('adminPanel.columns.legal')}</th><th>{t('adminPanel.columns.status')}</th><th style={{width:56}}></th></tr></thead><tbody>
          {filteredItems.map(i => (<tr key={i._id} {...rowProps(i)}><td><strong>{getLocalized(i.contractTypeId?.name, i18n.language) || '—'}</strong></td><td>{i.roleId?.roleName || '—'}</td><td>{i.firstManagerRoleId?.roleName || '—'}</td><td>{i.secondManagerRoleId?.roleName || '—'}</td><td>{i.thirdManagerRoleId?.roleName || '—'}</td><td>{i.requireLegalApproval ? <FiCheck className="text-success" /> : <span className="muted">—</span>}</td><td><StatusBadge active={i.isActive} /></td><td><Actions item={i} /></td></tr>))}
        </tbody></table>);
      default: return null;
    }
  };

  const currentTab = TABS.find(tb => tb.key === activeTab);
  const isWideModal = activeTab === 'contractTypes' || activeTab === 'flowControls' || activeTab === 'counterparties';

  return (
    <div className="admin-panel">
      {/* The parent menu name ("Contract Settings") is the small group label —
          never a second large heading. The title is the selected sidebar
          entry, i.e. the active tab. */}
      <PageHeader
        label={t('nav.businessConfig', 'Contract Settings')}
        labelIcon={<FiSettings />}
        title={currentTab?.label}
        description={t(`pageDescriptions.${activeTab}`, { defaultValue: '' })}
      />

      {/* CLM-49: no in-page category card — navigation lives in the app's
          main sidebar, which deep-links into tabs via ?tab=. */}
      <main className="admin-panel__content">
          {activeTab === 'templates' ? (
            <div className="admin-panel__embed"><ContractTemplates embedded /></div>
          ) : activeTab === 'templateLibrary' ? (
            <div className="admin-panel__embed"><TemplateLibraryAdmin /></div>
          ) : activeTab === 'schemas' ? (
            <div className="admin-panel__embed"><SchemaList embedded /></div>
          ) : (
          <div className="content-card">
            <div className="content-card__head">
              <div className="content-card__heading">
                <h3>{currentTab?.label}</h3>
              </div>
              <div className="content-card__actions">
                <div className="search-input">
                  <FiSearch />
                  <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder={t('adminPanel.searchPlaceholder', { tab: currentTab?.label?.toLowerCase() || '' })}
                  />
                </div>
                <button className="btn btn-primary btn-sm" onClick={openCreate}>
                  <FiPlus /> {t('adminPanel.newItem', { tab: currentTab?.singular })}
                </button>
              </div>
            </div>

            <div className="content-card__meta">
              <span>
                <strong>{filteredItems.length}</strong>
                <span>{query ? t('adminPanel.stats.matching') : t('adminPanel.stats.total')}</span>
              </span>
              <span className="divider" />
              <span>
                <span className="badge__dot badge__dot--active" />
                <strong>{activeCount}</strong>
                <span>{t('adminPanel.stats.active')}</span>
              </span>
              <span>
                <span className="badge__dot badge__dot--muted" />
                <strong>{safeItems.length - activeCount}</strong>
                <span>{t('adminPanel.stats.inactive')}</span>
              </span>
            </div>

            <div className="content-card__table">
              {renderTable()}
            </div>
          </div>
          )}
      </main>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        icon={currentTab?.icon}
        title={editItem
          ? t('adminPanel.modal.editTitle', { item: currentTab?.singular })
          : t('adminPanel.modal.newTitle',  { item: currentTab?.singular })}
        subtitle={editItem
          ? t('adminPanel.modal.editSubtitle')
          : t('adminPanel.modal.createSubtitle')}
        wide={isWideModal}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>{t('adminPanel.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('adminPanel.saving') : (editItem ? t('adminPanel.update') : t('adminPanel.create'))}
            </button>
          </>
        )}
      >
        {renderModalForm()}
      </Modal>
    </div>
  );
}

// ─── Small helpers ───
function FormSection({ title, children }) {
  return (
    <div className="form-section">
      <div className="form-section__title">{title}</div>
      {children}
    </div>
  );
}

function ActiveToggle({ value, onChange, label, name = 'isActive' }) {
  return (
    <label className={`toggle ${value ? 'toggle--on' : ''}`}>
      <input type="checkbox" name={name} checked={!!value} onChange={onChange} />
      <span className="toggle__switch"><span className="toggle__thumb" /></span>
      <span className="toggle__label">{label}</span>
    </label>
  );
}

export default AdminPanel;
